'use client'

import { useEffect, useCallback, useState } from 'react';
import { usePathname } from 'next/navigation';
import { trackAnalytics, getOrCreateSessionId } from '@/services/api';

// Analytics component that tracks page views automatically
export function Analytics() {
  const pathname = usePathname();
  const [searchParams, setSearchParams] = useState(null);
  
  // Initialize search params client-side only
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlSearchParams = new URLSearchParams(window.location.search);
      setSearchParams(urlSearchParams);
    }
  }, []);
  
  // Track page view when route changes
  useEffect(() => {
    if (searchParams) {
      // Get property code from URL if available
      const propertyCode = searchParams?.get('property') || searchParams?.get('property_code');
      
      // Track page view with error handling
      trackPageView({
        page: pathname,
        property_code: propertyCode || undefined,
        referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined
      }).catch(err => {
        // Silently catch analytics errors - they should never break the app
        console.warn('⚠️ Analytics page view tracking failed (non-critical)');
      });
    }
  }, [pathname, searchParams]);
  
  // Empty render - this is a utility component
  return null;
}

// Function to track page views
export const trackPageView = async (options = {}) => {
  try {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return { status: 'skipped', message: 'No session ID' };
    
    const eventData = {
      session_id: sessionId,
      page: options.page || window.location.pathname,
      event_type: 'view',
      event_label: options.event_label || 'page_view',
      property_code: options.property_code,
      referrer: options.referrer
    };
    
    return await trackAnalytics(eventData);
  } catch (error) {
    console.warn('⚠️ Analytics page view error (non-critical):', error.message);
    return { status: 'skipped', message: 'Error tracking page view' };
  }
};

// Function to track click events
export const trackClickEvent = async (eventLabel, options = {}) => {
  try {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return { status: 'skipped', message: 'No session ID' };
    
    const eventData = {
      session_id: sessionId,
      page: options.page || window.location.pathname,
      event_type: 'click',
      event_label: eventLabel,
      property_code: options.property_code,
      referrer: options.referrer
    };
    
    return await trackAnalytics(eventData);
  } catch (error) {
    console.warn('⚠️ Analytics click event error (non-critical):', error.message);
    return { status: 'skipped', message: 'Error tracking click event' };
  }
};

// Function to track form submissions
export const trackSubmitEvent = async (eventLabel, options = {}) => {
  try {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return { status: 'skipped', message: 'No session ID' };
    
    const eventData = {
      session_id: sessionId,
      page: options.page || window.location.pathname,
      event_type: 'submit',
      event_label: eventLabel,
      property_code: options.property_code,
      referrer: options.referrer
    };
    
    return await trackAnalytics(eventData);
  } catch (error) {
    console.warn('⚠️ Analytics submit event error (non-critical):', error.message);
    return { status: 'skipped', message: 'Error tracking submit event' };
  }
};

// Function to track booking events
export const trackBookEvent = async (reservationId, options = {}) => {
  try {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return { status: 'skipped', message: 'No session ID' };
    
    const eventData = {
      session_id: sessionId,
      page: options.page || window.location.pathname,
      event_type: 'book',
      event_label: `reservation_${reservationId}`,
      property_code: options.property_code,
      referrer: options.referrer
    };
    
    return await trackAnalytics(eventData);
  } catch (error) {
    console.warn('⚠️ Analytics book event error (non-critical):', error.message);
    return { status: 'skipped', message: 'Error tracking book event' };
  }
};

// Function to track cancel events
export const trackCancelEvent = async (reservationId, options = {}) => {
  try {
    const sessionId = getOrCreateSessionId();
    if (!sessionId) return { status: 'skipped', message: 'No session ID' };
    
    const eventData = {
      session_id: sessionId,
      page: options.page || window.location.pathname,
      event_type: 'cancel',
      event_label: `cancel_${reservationId}`,
      property_code: options.property_code,
      referrer: options.referrer
    };
    
    return await trackAnalytics(eventData);
  } catch (error) {
    console.warn('⚠️ Analytics cancel event error (non-critical):', error.message);
    return { status: 'skipped', message: 'Error tracking cancel event' };
  }
};

// Create a hook for tracking events in functional components
export const useAnalytics = () => {
  const pathname = usePathname();
  const [searchParams, setSearchParams] = useState(null);
  
  // Initialize search params client-side only
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlSearchParams = new URLSearchParams(window.location.search);
      setSearchParams(urlSearchParams);
    }
  }, []);
  
  // Get property code from URL if available
  const propertyCode = searchParams?.get('property') || searchParams?.get('property_code');
  
  // Create memoized tracking functions
  const trackPage = useCallback((label) => {
    return trackPageView({
      page: pathname,
      event_label: label,
      property_code: propertyCode
    });
  }, [pathname, propertyCode]);
  
  const trackClick = useCallback((label) => {
    return trackClickEvent(label, {
      page: pathname,
      property_code: propertyCode
    });
  }, [pathname, propertyCode]);
  
  const trackSubmit = useCallback((label) => {
    return trackSubmitEvent(label, {
      page: pathname,
      property_code: propertyCode
    });
  }, [pathname, propertyCode]);
  
  const trackBook = useCallback((reservationId) => {
    return trackBookEvent(reservationId, {
      page: pathname,
      property_code: propertyCode
    });
  }, [pathname, propertyCode]);
  
  const trackCancel = useCallback((reservationId) => {
    return trackCancelEvent(reservationId, {
      page: pathname,
      property_code: propertyCode
    });
  }, [pathname, propertyCode]);
  
  return {
    trackPage,
    trackClick,
    trackSubmit,
    trackBook,
    trackCancel
  };
};

export default Analytics; 